import React, { useState } from 'react';
import { CargoFranquia } from '../types/Funcionario';
import { useAuth } from '../contexts/AuthContext';

interface GerenciarCargosProps {
  cargosAtuais: CargoFranquia[];
  onSalvar: (novosCargos: CargoFranquia[]) => Promise<void>;
  onCancelar: () => void;
  loading?: boolean;
}

export const GerenciarCargos: React.FC<GerenciarCargosProps> = ({
  cargosAtuais,
  onSalvar,
  onCancelar,
  loading = false
}) => {
  const { user } = useAuth();
  const [cargosSelecionados, setCargosSelecionados] = useState<CargoFranquia[]>(cargosAtuais);
  const [error, setError] = useState<string>('');

  const todosOsCargos: CargoFranquia[] = ['ADMIN', 'GESTOR', 'FINANCEIRO', 'CADASTRAMENTO', 'VENDEDOR'];
  
  const cargoLabels: Record<CargoFranquia, string> = {
    'ADMIN': 'Administrador',
    'GESTOR': 'Gestor',
    'FINANCEIRO': 'Financeiro',
    'CADASTRAMENTO': 'Cadastramento',
    'VENDEDOR': 'Vendedor'
  };

  const cargoDescricoes: Record<CargoFranquia, string> = {
    'ADMIN': 'Acesso total ao sistema',
    'GESTOR': 'Gerenciamento de funcionários e operações',
    'FINANCEIRO': 'Gestão financeira e relatórios',
    'CADASTRAMENTO': 'Cadastro de imóveis e clientes',
    'VENDEDOR': 'Vendas e atendimento ao cliente'
  };

  // Verificar se o usuário atual pode adicionar GESTOR
  const podeAdicionarGestor = user?.franquia_membership?.cargos.includes('ADMIN');

  const handleCargoChange = (cargo: CargoFranquia, selecionado: boolean) => {
    setError('');
    
    if (selecionado) {
      // Adicionar cargo
      if (!cargosSelecionados.includes(cargo)) {
        setCargosSelecionados([...cargosSelecionados, cargo]);
      }
    } else {
      // Remover cargo
      const novosCargos = cargosSelecionados.filter(c => c !== cargo);
      
      // Validar se pelo menos um cargo permanece
      if (novosCargos.length === 0) {
        setError('O membro deve ter pelo menos um cargo');
        return;
      }
      
      setCargosSelecionados(novosCargos);
    }
  };

  const handleSalvar = async () => {
    if (cargosSelecionados.length === 0) {
      setError('O membro deve ter pelo menos um cargo');
      return;
    }

    try {
      await onSalvar(cargosSelecionados);
    } catch (error: any) {
      setError('Erro ao atualizar cargos');
    }
  };

  const cargoEstaDisponivel = (cargo: CargoFranquia): boolean => {
    // ADMIN só pode ser adicionado por outro ADMIN
    if (cargo === 'ADMIN' && !podeAdicionarGestor) {
      return false;
    }
    
    // GESTOR só pode ser adicionado por ADMIN
    if (cargo === 'GESTOR' && !podeAdicionarGestor) {
      return false;
    }
    
    return true;
  };

  const getCargoStatus = (cargo: CargoFranquia): 'selecionado' | 'disponivel' | 'bloqueado' => {
    if (cargosSelecionados.includes(cargo)) {
      return 'selecionado';
    }
    
    if (!cargoEstaDisponivel(cargo)) {
      return 'bloqueado';
    }
    
    return 'disponivel';
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Gerenciar Cargos
            </h3>
            <button
              onClick={onCancelar}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Lista de Cargos */}
          <div className="space-y-3 mb-6">
            {todosOsCargos.map((cargo) => {
              const status = getCargoStatus(cargo);
              const isDisabled = status === 'bloqueado' || loading;
              
              return (
                <div
                  key={cargo}
                  className={`p-4 border rounded-lg ${
                    status === 'selecionado' 
                      ? 'border-blue-500 bg-blue-50' 
                      : status === 'bloqueado'
                      ? 'border-gray-200 bg-gray-50'
                      : 'border-gray-300 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id={`cargo-${cargo}`}
                        type="checkbox"
                        checked={status === 'selecionado'}
                        onChange={(e) => handleCargoChange(cargo, e.target.checked)}
                        disabled={isDisabled}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label 
                        htmlFor={`cargo-${cargo}`} 
                        className={`font-medium ${
                          status === 'bloqueado' ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        {cargoLabels[cargo]}
                      </label>
                      <p className={`${
                        status === 'bloqueado' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {cargoDescricoes[cargo]}
                      </p>
                      {status === 'bloqueado' && (
                        <p className="text-red-500 text-xs mt-1">
                          {cargo === 'ADMIN' || cargo === 'GESTOR' 
                            ? 'Apenas administradores podem gerenciar este cargo'
                            : 'Cargo não disponível'
                          }
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resumo */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Cargos Selecionados:</h4>
            <div className="flex flex-wrap gap-2">
              {cargosSelecionados.map((cargo) => (
                <span
                  key={cargo}
                  className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                >
                  {cargoLabels[cargo]}
                </span>
              ))}
              {cargosSelecionados.length === 0 && (
                <span className="text-sm text-red-500">Nenhum cargo selecionado</span>
              )}
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancelar}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={loading || cargosSelecionados.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </div>
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
